import React, { useCallback, useState } from 'react';
import styled, { withTheme } from 'styled-components';
import { ColumnDef } from '../../../atoms/Table/tableFeatures';
import { ErrorType } from '../../../../types/response/ErrorCodes';
import ExpandableCard from '../../../atoms/Card/ExpandableCard';
import CardBody from '../../../atoms/Card/CardBody';
import ErrorMessage from '../../../atoms/Message/Error/ErrorMessage';
import Table from '../../../atoms/Table/Table';
import { TableRowContent } from '../../../../types/Layout/TableRowContent';
import { FailedWebhook } from '../../../../types/webhooks/FailedWebhook';
import { ResponseData } from '../../../../types/response/ResponseData';
import { formatDate } from '../../../../services/helpers/dateTimeFormats';
import TwoRowCell from '../../../atoms/Table/TwoRowCell';
import { Modal } from '../../../atoms/Modal/Modal';
import useModal from '../../../../services/hooks/useModal';
import FailedWebhookDetails from './FailedWebhookDetails';
import InformationMessage from '../../../atoms/Message/Information/InformationMessage';
import { useWebhooksStore } from '../../../../store/admin/webhooks/webhooksStore';
import useTableExpanded from '../../../../services/hooks/useTableExpanded';

interface SearchResultsProps {
    searchResults: ResponseData<FailedWebhook[]>;
}

interface FailedWebhookForTable extends FailedWebhook, TableRowContent {}

export const decodeWebhookType = (data: string) => {
    let type;
    const decodeErrorText = '<Unable to decode>';

    try {
        type = JSON.parse(data).Type;
    } catch (error) {
        type = decodeErrorText;
    }

    if (!type) {
        type = decodeErrorText;
    }
    return type;
};

const FailedWebhooksSearchResults = ({ searchResults }: SearchResultsProps) => {
    const { searchFailedWebhooksError, selectedFailedWebhooks, setSelectedFailedWebhooks } =
        useWebhooksStore();
    const [currentFailedWebhook, setCurrentFailedWebhook] = useState<FailedWebhook>();
    const failedWebhookDetailsModal = useModal();
    const { isTableExpanded, toggleTableExpanded } = useTableExpanded();

    const failedWebhooks = searchResults || [];
    const onRowClick = (row: FailedWebhook) => {
        setCurrentFailedWebhook(row);
        failedWebhookDetailsModal.toggle();
    };

    const onCloseFailedWebhookDetailsModal = () => {
        failedWebhookDetailsModal.toggle();
    };

    const setSelectedRowsCallBack = useCallback(
        (failedWebhooks: FailedWebhook[]) => {
            if (selectedFailedWebhooks.length !== failedWebhooks.length) {
                setSelectedFailedWebhooks(failedWebhooks);
            }
        },
        [setSelectedFailedWebhooks, selectedFailedWebhooks]
    );

    const failedWebhookColumns: ColumnDef<FailedWebhookForTable>[] = [
        {
            header: 'Timestamp',
            accessorKey: 'timestamp',
            enableSorting: false,
            cell: (props: { row: { original: FailedWebhook } }) => {
                const { timestamp } = props.row.original.action;

                const timeArray = formatDate(new Date(timestamp * 1000));
                return <TwoRowCell upper={timeArray.yyyyMMdd} lower={timeArray.HHmm} />;
            },
        },
        {
            header: 'Type',
            accessorKey: 'type',
            enableSorting: false,
            cell: (props: { row: { original: FailedWebhook } }) => {
                const { data } = props.row.original.action;

                return <span>{decodeWebhookType(data)}</span>;
            },
        },
        {
            header: 'Webhook endpoint',
            accessorKey: 'url',
            enableSorting: false,
            cell: (props: { row: { original: FailedWebhook } }) => {
                const { url } = props.row.original.action;

                if (!url || url.length === 0) {
                    return <span>None</span>;
                }

                return <span>{url}</span>;
            },
        },
    ];

    if (searchFailedWebhooksError) {
        return (
            <ErrorMessage error={searchFailedWebhooksError} errorHeader={ErrorType.FailedWebhook} />
        );
    }

    if (failedWebhooks.data.length === 0) {
        return (
            <MessageWrapper>
                <InformationMessage
                    messages={['No failed webhooks found.']}
                    messageHeader="Empty list"
                />
            </MessageWrapper>
        );
    }

    return (
        <>
            {currentFailedWebhook && (
                <Modal
                    mediumLarge
                    isShown={failedWebhookDetailsModal.isShown}
                    hide={onCloseFailedWebhookDetailsModal}
                >
                    <FailedWebhookDetails
                        failedWebhook={currentFailedWebhook}
                        hide={onCloseFailedWebhookDetailsModal}
                    />
                </Modal>
            )}
            <ExpandableCard expanded={isTableExpanded}>
                <CardBody>
                    <Table<FailedWebhookForTable>
                        data={failedWebhooks.data as FailedWebhookForTable[]}
                        columns={failedWebhookColumns}
                        onRowClicked={onRowClick}
                        onSelectedRowsChange={setSelectedRowsCallBack}
                        isExpanded={isTableExpanded}
                        onExpandToggle={toggleTableExpanded}
                    />
                </CardBody>
            </ExpandableCard>
        </>
    );
};

const MessageWrapper = styled.div`
    margin: 2rem;
`;

export default withTheme(FailedWebhooksSearchResults) as React.ComponentType<
    Omit<SearchResultsProps, 'theme'>
>;
